import { Injectable } from '@angular/core';
import { Observable, interval } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

@Injectable()
export class CountdownTimerService {
  /**
   * Parses a date string in DD/MM/YYYY format and returns a Date object
   * The time will be set to the current time of day
   * @param dateString - Date string in format "DD/MM/YYYY"
   * @returns Date object
   */
  private parseDateString(dateString: string): Date {
    const parts = dateString.split('/');

    if (parts.length !== 3) {
      throw new Error('Invalid date format. Expected DD/MM/YYYY');
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed in JavaScript
    const year = parseInt(parts[2], 10);

    // Validate the parsed values
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      throw new Error('Invalid date values in string');
    }

    if (day < 1 || day > 31) {
      throw new Error('Invalid day value. Must be between 1 and 31');
    }

    if (month < 0 || month > 11) {
      throw new Error('Invalid month value. Must be between 1 and 12');
    }

    if (year < 1900 || year > 2100) {
      throw new Error('Invalid year value. Must be between 1900 and 2100');
    }

    // Create target date at midnight (00:00:00)
    // This ensures a fixed deadline that doesn't change on page refresh
    const targetDate = new Date(year, month, day, 0, 0, 0, 0);

    return targetDate;
  }

  /**
   * Calculates the time remaining between now and the target date
   * @param targetDate - The target date to count down to
   * @returns TimeRemaining object
   */
  private calculateTimeRemaining(targetDate: Date): TimeRemaining {
    const now = new Date().getTime();
    const target = targetDate.getTime();
    const difference = target - now;

    // If the countdown has expired
    if (difference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true
      };
    }

    // Calculate time units
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      isExpired: false
    };
  }

  /**
   * Starts the countdown timer and returns an Observable that emits every second
   * @param dateString - Date string in format "DD/MM/YYYY"
   * @returns Observable<TimeRemaining> that emits every second
   */
  public startCountdown(dateString: string): Observable<TimeRemaining> {
    let targetDate: Date;

    try {
      targetDate = this.parseDateString(dateString);
    } catch (error) {
      console.error('Error parsing date string:', error);
      // Return an Observable that emits an expired state
      return new Observable<TimeRemaining>(observer => {
        observer.next({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true
        });
      });
    }

    // Create an Observable that emits every second (1000ms)
    return interval(1000).pipe(
      startWith(0), // Emit immediately on subscription
      map(() => this.calculateTimeRemaining(targetDate))
    );
  }

  /**
   * Formats the parsed date for display
   * @param dateString - Date string in format "DD/MM/YYYY"
   * @returns Formatted date string
   */
  public formatDateForDisplay(dateString: string): string {
    try {
      const targetDate = this.parseDateString(dateString);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      };
      return targetDate.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }
}
